import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "search", "optionsContainer", "button", "displayText", "option", "group", "selectedContainer", "selectedBadge", "ajaxPlaceholder", "ajaxResults", "ajaxLoading", "hiddenInput", "selectedBadges", "searchClear", "staticCheckbox"]
  static values = {
    placeholder: { type: String, default: "Select..." },
    ajaxUrl: String,
    name: String
  }

  connect() {
    this.debounceTimer = null
    this.page = 1
    this.hasMore = true
    this.isLoading = false
    this.searchTerm = ''
    this.hasLoadedOnce = false

    // Initialize display based on mode
    if (this.isAjaxMode) {
      this.updateAjaxDisplay()

      // Load initial results when dropdown opens
      this.element.addEventListener('shown.bs.dropdown', () => {
        if (!this.hasLoadedOnce) {
          this.loadAjaxResults()
          this.hasLoadedOnce = true
        }
        // Focus search input
        if (this.hasSearchTarget) {
          this.searchTarget.focus()
        }
      })
    } else {
      this.updateDisplay()
    }
  }

  get isAjaxMode() {
    return this.hasAjaxUrlValue && this.ajaxUrlValue
  }

  // Infinite scroll handler
  onScroll(event) {
    if (!this.isAjaxMode) return

    const element = event.target
    const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50

    if (isNearBottom && this.hasMore && !this.isLoading) {
      this.page++
      this.loadAjaxResults()
    }
  }

  removeSelected(event) {
    event.preventDefault()
    event.stopPropagation()
    const value = event.currentTarget.dataset.value

    if (this.isAjaxMode) {
      // Uncheck the checkbox in the results (and any pinned static option)
      const checkboxes = this.ajaxResultsTarget.querySelectorAll('input[type="checkbox"]')
      checkboxes.forEach(checkbox => {
        if (checkbox.value === value) {
          checkbox.checked = false
        }
      })
      this.staticCheckboxTargets.forEach(checkbox => {
        if (checkbox.value === value) {
          checkbox.checked = false
        }
      })

      // Remove the hidden input
      this.hiddenInputTargets.forEach(input => {
        if (input.dataset.value === value) {
          input.remove()
        }
      })

      this.updateAjaxDisplay()
    } else {
      // Uncheck the corresponding checkbox
      this.checkboxTargets.forEach(checkbox => {
        if (checkbox.value === value) {
          checkbox.checked = false
        }
      })

      this.updateDisplay()
      this.updateSelectedBadges()
    }
  }

  removeSelectedFromButton(event) {
    event.preventDefault()
    event.stopPropagation()

    // Call the same removal logic
    this.removeSelected(event)
  }

  // Search clear button
  clearSearch() {
    this.searchTarget.value = ''
    this.updateSearchClearVisibility()

    if (this.isAjaxMode) {
      this.searchTerm = ''
      this.page = 1
      this.hasMore = true
      this.loadAjaxResults(true)
    } else {
      this.filter()
    }
  }

  updateSearchClearVisibility() {
    if (!this.hasSearchClearTarget) return

    if (this.searchTarget.value.length > 0) {
      this.searchClearTarget.classList.remove('d-none')
    } else {
      this.searchClearTarget.classList.add('d-none')
    }
  }

  // AJAX Mode Methods
  ajaxSearch() {
    if (!this.isAjaxMode) return

    const query = this.searchTarget.value.trim()
    this.updateSearchClearVisibility()

    // Clear any existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.searchTerm = query
      this.page = 1
      this.hasMore = true
      this.loadAjaxResults(true)
    }, 300)
  }

  async loadAjaxResults(isSearch = false) {
    if (this.isLoading) return

    this.isLoading = true

    // Show loading indicator
    this.ajaxPlaceholderTarget.classList.add('d-none')
    if (isSearch || this.page === 1) {
      this.ajaxResultsTarget.classList.add('d-none')
    }
    this.ajaxLoadingTarget.classList.remove('d-none')

    try {
      const url = new URL(this.ajaxUrlValue, window.location.origin)
      url.searchParams.set('page', this.page)
      if (this.searchTerm) {
        url.searchParams.set('term', this.searchTerm)
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Network response was not ok')

      const data = await response.json()

      // Handle paginated response format
      const results = data.results || data
      this.hasMore = data.pagination?.more || false

      if (isSearch || this.page === 1) {
        this.ajaxResultsTarget.innerHTML = ''
      }

      this.renderAjaxResults(results)
    } catch (error) {
      console.error('AJAX search error:', error)
      this.ajaxResultsTarget.innerHTML = '<div class="text-danger small text-center py-2">Error loading results</div>'
      this.ajaxResultsTarget.classList.remove('d-none')
    } finally {
      this.isLoading = false
      this.ajaxLoadingTarget.classList.add('d-none')
    }
  }

  renderAjaxResults(data) {
    // Get currently selected values to pre-check them
    const selectedValues = new Set(
      this.hiddenInputTargets.map(input => input.value)
    )

    if (data.length === 0 && this.ajaxResultsTarget.children.length === 0) {
      this.ajaxPlaceholderTarget.classList.remove('d-none')
      this.ajaxResultsTarget.classList.add('d-none')
      return
    }

    // Build checkbox options from results and append
    // Data format is [[name, id], [name, id], ...]
    data.forEach(([name, id]) => {
      const isSelected = selectedValues.has(String(id))
      const div = document.createElement('div')
      div.className = 'form-check'
      div.dataset.multiSelectDropdownTarget = 'option'
      div.innerHTML = `
        <label class="form-check-label d-flex align-items-center gap-2">
          <input class="form-check-input m-0"
                 type="checkbox"
                 value="${id}"
                 data-label="${name}"
                 ${isSelected ? 'checked' : ''}
                 data-action="change->multi-select-dropdown#onAjaxCheckboxChange">
          ${name}
        </label>
      `
      this.ajaxResultsTarget.appendChild(div)
    })

    this.ajaxResultsTarget.classList.remove('d-none')
    this.ajaxPlaceholderTarget.classList.add('d-none')
  }

  onAjaxCheckboxChange(event) {
    const checkbox = event.target
    const value = checkbox.value
    const label = checkbox.dataset.label

    if (checkbox.checked) {
      // Add to selected
      this.addAjaxSelection(value, label)
    } else {
      // Remove from selected
      this.removeAjaxSelection(value)
    }
  }

  removeAjaxSelection(value) {
    // Remove the hidden input
    this.hiddenInputTargets.forEach(input => {
      if (input.dataset.value === value) {
        input.remove()
      }
    })

    this.updateAjaxDisplay()
  }

  addAjaxSelection(value, label) {
    // Get the form name from the value or existing hidden inputs
    const name = this.nameValue || this.hiddenInputTargets[0]?.name || 'items[]'

    // Add hidden input
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    input.dataset.multiSelectDropdownTarget = 'hiddenInput'
    input.dataset.value = value
    input.dataset.label = label
    this.element.appendChild(input)

    this.updateAjaxDisplay()
  }

  updateAjaxDisplay() {
    const count = this.hiddenInputTargets.length

    if (count === 0) {
      this.displayTextTarget.innerHTML = this.placeholderValue
    } else {
      // Get labels and values from hidden inputs
      const items = this.hiddenInputTargets.map(input => ({
        label: input.dataset.label || input.value,
        value: input.value
      }))

      let html = ''
      if (count <= 2) {
        items.forEach(item => {
          html += this.buildButtonBadge(item.label, item.value)
        })
      } else {
        // Show "N selected" with clear button
        html = `<span class="bg-light text-dark rounded px-1 py-0 d-inline-flex align-items-center">${count} selected<span class="text-secondary ms-1" style="cursor: pointer;" data-action="click->multi-select-dropdown#clearAllAjax"><i class="fa fa-times-circle"></i></span></span>`
      }

      this.displayTextTarget.innerHTML = html
    }
  }

  buildButtonBadge(label, value) {
    return `
      <span class="bg-light text-dark rounded px-1 py-0 d-inline-flex align-items-center">
        ${label}
        <span class="text-secondary ms-1" style="cursor: pointer;" data-action="click->multi-select-dropdown#removeSelectedFromButton" data-value="${value}"><i class="fa fa-times-circle"></i></span>
      </span>
    `
  }

  clearAllAjax() {
    // Uncheck all checkboxes in the results and any pinned static options
    const checkboxes = this.ajaxResultsTarget.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = false
    })
    this.staticCheckboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })

    // Remove all hidden inputs
    this.hiddenInputTargets.forEach(input => input.remove())

    this.updateAjaxDisplay()
  }

  updateSelectedBadges() {
    if (!this.hasSelectedContainerTarget) return

    const checked = this.checkboxTargets.filter(cb => cb.checked && !cb.disabled)
    const container = this.selectedContainerTarget
    const badgesContainer = container.querySelector('.d-flex.flex-wrap')

    if (checked.length === 0) {
      container.classList.add('d-none')
    } else {
      container.classList.remove('d-none')

      // Rebuild badges, but deduplicate by label
      badgesContainer.innerHTML = ''
      const seenLabels = new Set()

      checked.forEach(cb => {
        const label = cb.dataset.label || cb.value

        // Skip if we've already shown this label
        if (seenLabels.has(label)) return
        seenLabels.add(label)

        const badge = document.createElement('span')
        badge.className = 'bg-light rounded-pill text-dark d-inline-flex align-items-center gap-1 px-1'
        badge.dataset.multiSelectDropdownTarget = 'selectedBadge'
        badge.dataset.value = cb.value
        badge.innerHTML = `
          ${label}
          <span class="text-secondary" style="cursor: pointer;" data-action="click->multi-select-dropdown#removeSelected" data-value="${cb.value}"><i class="fa fa-times-circle"></i></span>
        `
        badgesContainer.appendChild(badge)
      })
    }
  }

  filter() {
    const query = this.searchTarget.value.toLowerCase()
    this.updateSearchClearVisibility()

    this.optionTargets.forEach(option => {
      const label = option.querySelector('label')?.textContent.toLowerCase() || ''
      const isVisible = label.includes(query)
      option.classList.toggle('d-none', !isVisible)
    })

    // Hide empty groups (groups force-hidden from outside stay hidden
    // even when their options match the search)
    if (this.hasGroupTarget) {
      this.groupTargets.forEach(group => {
        const visibleOptions = group.querySelectorAll('.form-check:not(.d-none)')
        group.classList.toggle('d-none', visibleOptions.length === 0 || group.dataset.forceHidden === 'true')
      })
    }
  }

  selectAll() {
    this.checkboxTargets.forEach(checkbox => {
      if (!checkbox.disabled && !checkbox.closest('.form-check').classList.contains('d-none')) {
        checkbox.checked = true
      }
    })
    this.updateDisplay()
    this.updateSelectedBadges()
  }

  clearAll() {
    this.checkboxTargets.forEach(checkbox => checkbox.checked = false)
    this.updateDisplay()
    this.updateSelectedBadges()
  }

  selectGroup(event) {
    const group = event.currentTarget.dataset.group
    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.dataset.group === group) {
        checkbox.checked = true
      }
    })
    this.updateDisplay()
    this.updateSelectedBadges()
  }

  clearGroup(event) {
    const group = event.currentTarget.dataset.group
    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.dataset.group === group) {
        checkbox.checked = false
      }
    })
    this.updateDisplay()
    this.updateSelectedBadges()
  }

  onCheckboxChange() {
    this.updateDisplay()
    this.updateSelectedBadges()
  }

  updateDisplay() {
    // Disabled checkboxes don't submit, so they don't count as selected
    const checked = this.checkboxTargets.filter(cb => cb.checked && !cb.disabled)

    // Deduplicate by label and collect items
    const seenLabels = new Set()
    const items = []
    checked.forEach(cb => {
      const label = cb.dataset.label || cb.value
      if (!seenLabels.has(label)) {
        seenLabels.add(label)
        items.push({ label, value: cb.value })
      }
    })

    const count = items.length

    if (count === 0) {
      this.displayTextTarget.innerHTML = this.placeholderValue
    } else {
      let html = ''
      if (count <= 2) {
        items.forEach(item => {
          html += this.buildButtonBadge(item.label, item.value)
        })
      } else {
        // Show "N selected" with clear button
        html = `<span class="bg-light text-dark rounded px-1 py-0 d-inline-flex align-items-center">${count} selected<span class="text-secondary ms-1" style="cursor: pointer;" data-action="click->multi-select-dropdown#clearAll"><i class="fa fa-times-circle"></i></span></span>`
      }

      this.displayTextTarget.innerHTML = html
    }
  }
}
